/*
 * Copyright (c) 2024. Bolmate B.V.
 */

import React, {useContext, useEffect, useState} from 'react';
import {BolSearchTerm, DataSplitMethod, SearchVolumeData, SearchVolumePeriodType} from '../../util/interfaces';
import {AppContext} from '../misc/appContext';
import {getSearchVolumes, getSearchVolumesAIAnalysis} from '../../util/requestSender';
import {ResponseData, showDialog} from '@dvrd/dvr-controls';
import {createErrorMessage} from '../../util/utils';
import SearchVolumePage from './searchVolumePage';
import {useIsFirstRender} from '../../util/componentUtil';
import {clearNavigationParam, getNavigationParam} from '../../util/navigateUtil';
import {onSetCrumbs} from '../../util/events';
import {SEARCH_VOLUMES_ROUTE} from '../../util/constants';
import {useTranslation} from 'react-i18next';

function periodFromType(periodType: SearchVolumePeriodType): [DataSplitMethod, number] {
    if (periodType === SearchVolumePeriodType.LAST_WEEK) return [DataSplitMethod.DAY, 7];
    else if (periodType === SearchVolumePeriodType.LAST_14_DAYS) return [DataSplitMethod.DAY, 14];
    else if (periodType === SearchVolumePeriodType.LAST_MONTH) return [DataSplitMethod.DAY, 31];
    else if (periodType === SearchVolumePeriodType.LAST_3_MONTHS) return [DataSplitMethod.WEEK, 12];
    else if (periodType === SearchVolumePeriodType.LAST_6_MONTHS) return [DataSplitMethod.MONTH, 6];
    else if (periodType === SearchVolumePeriodType.LAST_YEAR) return [DataSplitMethod.MONTH, 12];
    else return [DataSplitMethod.MONTH, 24];
}

const AICache: Map<string, string> = new Map();

export default function SearchVolumeController() {
    const {account} = useContext(AppContext).accountContext;
    const {t} = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [searchTerms, setSearchTerms] = useState<Array<string>>([]);
    const [searchVolumes, setSearchVolumes] = useState<Array<SearchVolumeData>>([]);
    const [comparingVolumes, setComparingVolumes] = useState<Array<SearchVolumeData>>([]);
    const [historicVolumes, setHistoricVolumes] = useState<Array<SearchVolumeData>>([]);
    const [relatedTerms, setRelatedTerms] = useState<Array<BolSearchTerm>>([]);
    const [periodType, setPeriodType] = useState<SearchVolumePeriodType>(SearchVolumePeriodType.LAST_YEAR);
    const [loading, setLoading] = useState(false);
    const [loadingHistoric, setLoadingHistoric] = useState(false);
    const [combineTerms, setCombineTerms] = useState(true);
    const [aiSearchTerm, setAiSearchTerm] = useState('');
    const [loadingAI, setLoadingAI] = useState(false);
    const [aiAnalysis, setAiAnalysis] = useState('');
    const isFirstRender = useIsFirstRender();

    function onChangeAISearchTerm(searchTerm: string) {
        // Prevent analysing the same item
        if (searchTerm === aiSearchTerm) return;
        setAiSearchTerm(searchTerm);
    }

    function onChangeCombineTerms() {
        setCombineTerms(!combineTerms);
        focusInput();
    }

    function onChangeSearchTerm(value: string) {
        setSearchTerm(value);
    }

    function onEnterSearchTerm() {
        const term = searchTerm.trim();
        if (term.length) {
            if (combineTerms) setSearchTerms(searchTerms.concat(term));
            else setSearchTerms([term]);
        }
        setSearchTerm('');
    }

    function onRemoveTerm(term: string | null) {
        return function () {
            const terms = !term ? [] : searchTerms.filter(searchTerm => searchTerm !== term);
            if (!terms.length) {
                setSearchVolumes([]);
                setComparingVolumes([]);
                setHistoricVolumes([]);
                setRelatedTerms([]);
                setAiSearchTerm('');
                setAiAnalysis('');
            } else if (term === aiSearchTerm) setAiSearchTerm('');
            setSearchTerms(terms);
            focusInput();
        };
    }

    function onChangePeriod(value: SearchVolumePeriodType) {
        setPeriodType(value);
        focusInput();
    }

    function onClickRelated(term: string) {
        return function () {
            if (combineTerms) setSearchTerms(searchTerms.concat(term));
            else setSearchTerms([term]);
            focusInput();
        };
    }

    function focusInput() {
        document.getElementById('search-volume-term-field-input')?.focus();
    }

    function loadData() {
        return new Promise<void>(resolve => {
            if (!account || !searchTerms.length) return;
            setLoading(true);
            const [period, numberOfPeriods] = periodFromType(periodType);
            getSearchVolumes({
                data: {
                    account_id: account.id,
                    search_terms: searchTerms,
                    period,
                    number_of_periods: numberOfPeriods,
                    with_comparison: true
                },
                callback: (data: ResponseData) => {
                    setLoading(false);
                    if (data.success) {
                        const {related_terms, search_volume_data, comparison_search_volume_data} = data;
                        setRelatedTerms(related_terms);
                        setSearchVolumes(search_volume_data);
                        setComparingVolumes(comparison_search_volume_data);
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t('searchVolume.loadFailedMessage', 'Het laden van de zoekvolume is niet gelukt.')
                            ),
                            t('searchVolume.loadFailedTitle', 'Laden mislukt')
                        );
                    resolve();
                }
            });
        });
    }

    function loadHistoricData() {
        return new Promise<void>(resolve => {
            if (!account || !searchTerms.length) return;
            setLoadingHistoric(true);
            getSearchVolumes({
                data: {
                    account_id: account.id,
                    search_terms: searchTerms,
                    period: DataSplitMethod.MONTH,
                    number_of_periods: 48
                },
                callback: (data: ResponseData) => {
                    setLoadingHistoric(false);
                    if (data.success) {
                        const {search_volume_data} = data;
                        setHistoricVolumes(search_volume_data);
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t('searchVolume.loadFailedMessage', 'Het laden van de zoekvolume is niet gelukt.')
                            ),
                            t('searchVolume.loadFailedTitle', 'Laden mislukt')
                        );
                    resolve();
                }
            });
        });
    }

    function loadAIAnalysis() {
        if (!account || !aiSearchTerm.length) return;
        if (AICache.has(aiSearchTerm)) {
            setAiAnalysis(AICache.get(aiSearchTerm)!);
            return;
        }
        setLoadingAI(true);
        getSearchVolumesAIAnalysis({
            data: {search_term: aiSearchTerm, account_id: account.id},
            callback: (data: ResponseData) => {
                setLoadingAI(false);
                if (data.success) {
                    let {analysis} = data;
                    if (!/^.*[!?.]$/.test(analysis)) analysis = analysis.substring(0, analysis.lastIndexOf('.') + 1);
                    AICache.set(aiSearchTerm, analysis);
                    setAiAnalysis(analysis);
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('searchVolume.analyzeFailedMessage', 'Het analyseren van de data is niet gelukt.')
                        ),
                        t('searchVolume.analyzeFailedTitle', 'Analyse mislukt')
                    );
            }
        });
    }

    useEffect(() => {
        if (isFirstRender) {
            const navParam = getNavigationParam();
            if (navParam && Array.isArray(navParam)) {
                const terms: Array<string> = navParam.filter((item: any) => typeof item === 'string');
                setSearchTerms(terms);
                if (terms.length > 1) setCombineTerms(true);
                clearNavigationParam();
            }
        } else Promise.all([loadData(), loadHistoricData()]);
    }, [searchTerms, periodType]);

    useEffect(() => {
        loadAIAnalysis();
    }, [aiSearchTerm]);

    useEffect(() => {
        onSetCrumbs([{label: t('searchVolume.crumb', 'Zoekvolume'), route: SEARCH_VOLUMES_ROUTE}]);
    }, []);

    return (
        <SearchVolumePage
            onChangeSearchTerm={onChangeSearchTerm}
            onSearch={onEnterSearchTerm}
            onClickRelated={onClickRelated}
            onChangePeriod={onChangePeriod}
            onRemoveTerm={onRemoveTerm}
            onChangeCombineTerms={onChangeCombineTerms}
            onChangeAISearchTerm={onChangeAISearchTerm}
            searchTerm={searchTerm}
            searchTerms={searchTerms}
            searchVolumes={searchVolumes}
            comparingVolumes={comparingVolumes}
            historicVolumes={historicVolumes}
            relatedTerms={relatedTerms}
            periodType={periodType}
            loading={loading}
            loadingHistoric={loadingHistoric}
            combineTerms={combineTerms}
            aiSearchTerm={aiSearchTerm}
            loadingAI={loadingAI}
            aiAnalysis={aiAnalysis}
        />
    );
}
